import ExcelJS from 'exceljs';
import { useState, useCallback } from 'react';
import { richTextToMarkdown } from '../utils/richTextToMarkdown';
import type {
  ParsedFaqRow,
  ParsedSymptomRow,
  ValidationError,
  ParseResult,
  NewCategoryInfo,
} from '../types';
import type { FaqCategory, FaqItem, SymptomListItem } from '../../../types';

export interface RawFaqRow {
  rowNumber: number;
  categoryName: string;
  faqCategoryName: string;
  question: string;
  answer: string;
  isInitial: boolean;
}

export interface RawSymptomRow {
  rowNumber: number;
  categoryName: string;
  symptomName: string;
  cause: string;
  solution: string;
}

function cellToString(value: ExcelJS.CellValue): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'object') {
    // RichText: { richText: [{ text: '...' }, ...] }
    if ('richText' in value && Array.isArray((value as ExcelJS.CellRichTextValue).richText)) {
      return (value as ExcelJS.CellRichTextValue).richText.map((rt) => rt.text).join('');
    }
    // Hyperlink: { text: '...', hyperlink: '...' }
    if ('text' in value) {
      return String((value as ExcelJS.CellHyperlinkValue).text ?? '');
    }
    return String(value);
  }
  return String(value);
}

export async function parseExcelBuffer(buffer: ArrayBuffer): Promise<{
  rawFaqs: RawFaqRow[];
  rawSymptoms: RawSymptomRow[];
}> {
  const wb = new ExcelJS.Workbook();
  await wb.xlsx.load(buffer);

  const faqSheet = wb.getWorksheet('FAQ');
  const symptomSheet = wb.getWorksheet('症状');

  if (!faqSheet && !symptomSheet) {
    throw new Error('「FAQ」または「症状」シートが見つかりません');
  }

  const rawFaqs: RawFaqRow[] = [];
  const rawSymptoms: RawSymptomRow[] = [];

  if (faqSheet) {
    faqSheet.eachRow((row, rowNumber) => {
      if (rowNumber <= 1) return;
      const cells = [
        cellToString(row.getCell(1).value),
        cellToString(row.getCell(2).value),
        cellToString(row.getCell(3).value),
        richTextToMarkdown(row.getCell(4).value),
        cellToString(row.getCell(5).value),
      ];
      if (cells.every((c) => c === '')) return;
      rawFaqs.push({
        rowNumber,
        categoryName: cells[0].trim(),
        faqCategoryName: cells[1].trim(),
        question: cells[2].trim(),
        answer: cells[3].trim(),
        isInitial: cells[4].trim() === '○',
      });
    });
  }

  if (symptomSheet) {
    symptomSheet.eachRow((row, rowNumber) => {
      if (rowNumber <= 1) return;
      const cells = [
        cellToString(row.getCell(1).value),
        cellToString(row.getCell(2).value),
        richTextToMarkdown(row.getCell(3).value),
        richTextToMarkdown(row.getCell(4).value),
      ];
      if (cells.every((c) => c === '')) return;
      rawSymptoms.push({
        rowNumber,
        categoryName: cells[0].trim(),
        symptomName: cells[1].trim(),
        cause: cells[2].trim(),
        solution: cells[3].trim(),
      });
    });
  }

  return { rawFaqs, rawSymptoms };
}

export function validateFaqRows(rows: RawFaqRow[]): ValidationError[] {
  const errors: ValidationError[] = [];
  const questionMap = new Map<string, number>();

  for (const row of rows) {
    const rowErrors: ValidationError[] = [];
    if (!row.categoryName)
      rowErrors.push({
        sheet: 'faq',
        rowNumber: row.rowNumber,
        message: `行${row.rowNumber}: 製品カテゴリが未入力です`,
      });
    if (!row.faqCategoryName)
      rowErrors.push({
        sheet: 'faq',
        rowNumber: row.rowNumber,
        message: `行${row.rowNumber}: FAQカテゴリが未入力です`,
      });
    if (!row.question)
      rowErrors.push({
        sheet: 'faq',
        rowNumber: row.rowNumber,
        message: `行${row.rowNumber}: 質問が未入力です`,
      });
    if (!row.answer)
      rowErrors.push({
        sheet: 'faq',
        rowNumber: row.rowNumber,
        message: `行${row.rowNumber}: 回答が未入力です`,
      });

    // 必須フィールドエラーがない場合のみ重複チェックを行う
    if (rowErrors.length === 0 && row.question) {
      const key = `${row.categoryName}::${row.faqCategoryName}::${row.question}`;
      const existing = questionMap.get(key);
      if (existing !== undefined) {
        rowErrors.push({
          sheet: 'faq',
          rowNumber: row.rowNumber,
          message: `行${row.rowNumber}: 行${existing}と質問が重複しています`,
        });
      } else {
        questionMap.set(key, row.rowNumber);
      }
    }

    errors.push(...rowErrors);
  }
  return errors;
}

export function validateSymptomRows(rows: RawSymptomRow[]): ValidationError[] {
  const errors: ValidationError[] = [];
  const symptomMap = new Map<string, number>();

  for (const row of rows) {
    const rowErrors: ValidationError[] = [];
    if (!row.categoryName)
      rowErrors.push({
        sheet: 'symptom',
        rowNumber: row.rowNumber,
        message: `行${row.rowNumber}: 製品カテゴリが未入力です`,
      });
    if (!row.symptomName)
      rowErrors.push({
        sheet: 'symptom',
        rowNumber: row.rowNumber,
        message: `行${row.rowNumber}: 症状名が未入力です`,
      });
    if (!row.solution)
      rowErrors.push({
        sheet: 'symptom',
        rowNumber: row.rowNumber,
        message: `行${row.rowNumber}: 対処法が未入力です`,
      });

    // 必須フィールドエラーがない場合のみ重複チェックを行う
    if (rowErrors.length === 0 && row.categoryName && row.symptomName) {
      const key = `${row.categoryName}::${row.symptomName}`;
      const existing = symptomMap.get(key);
      if (existing !== undefined) {
        rowErrors.push({
          sheet: 'symptom',
          rowNumber: row.rowNumber,
          message: `行${row.rowNumber}: 行${existing}と症状名が重複しています`,
        });
      } else {
        symptomMap.set(key, row.rowNumber);
      }
    }

    errors.push(...rowErrors);
  }
  return errors;
}

export function buildParseResult(
  rawFaqs: RawFaqRow[],
  rawSymptoms: RawSymptomRow[],
  faqErrors: ValidationError[],
  symptomErrors: ValidationError[],
  existingCategories: FaqCategory[],
  existingFaqs: FaqItem[],
  existingSymptoms: SymptomListItem[],
): ParseResult {
  const allErrors = [...faqErrors, ...symptomErrors];
  const errorRowsFaq = new Set(faqErrors.map((e) => e.rowNumber));
  const errorRowsSymptom = new Set(symptomErrors.map((e) => e.rowNumber));

  const productCategories = existingCategories.filter((c) => !c.productCategoryId);
  const faqCategories = existingCategories.filter((c) => c.productCategoryId);
  const productCategoryNameSet = new Set(productCategories.map((c) => c.name));
  const faqCategoryNameMap = new Map<string, string>();
  for (const sub of faqCategories) {
    const parent = productCategories.find((p) => p.id === sub.productCategoryId);
    if (parent) faqCategoryNameMap.set(`${parent.name}::${sub.name}`, sub.id);
  }

  // 既存 FAQ の重複検出: faqCategoryId → 製品カテゴリ名・FAQカテゴリ名に変換してキーを構築
  const categoryIdToNameMap = new Map<string, string>();
  for (const cat of [...productCategories, ...faqCategories]) {
    categoryIdToNameMap.set(cat.id, cat.name);
  }
  const faqIdToProductNameMap = new Map<string, string>();
  for (const sub of faqCategories) {
    if (sub.productCategoryId) {
      faqIdToProductNameMap.set(sub.id, categoryIdToNameMap.get(sub.productCategoryId) ?? '');
    }
  }
  const existingFaqKeySet = new Set(
    existingFaqs.map((f) => {
      const subName = categoryIdToNameMap.get(f.faqCategoryId) ?? '';
      const parentName = faqIdToProductNameMap.get(f.faqCategoryId) ?? '';
      return `${parentName}::${subName}::${f.question}`;
    }),
  );
  // 症状の重複検出: categoryId → categoryName に変換して Excel の categoryName とマッチングする
  const categoryIdToName = new Map<string, string>();
  for (const cat of productCategories) {
    categoryIdToName.set(cat.id, cat.name);
  }
  const existingSymptomKeySet = new Set(
    existingSymptoms.map((s) => {
      const catName = categoryIdToName.get(s.categoryId) ?? s.categoryId;
      return `${catName}::${s.symptomName}`;
    }),
  );

  const newCategories: NewCategoryInfo[] = [];
  const seenNewParents = new Set<string>();
  const seenNewSubs = new Set<string>();

  const faqs: ParsedFaqRow[] = rawFaqs.map((raw) => {
    if (errorRowsFaq.has(raw.rowNumber)) {
      const err = allErrors.find((e) => e.sheet === 'faq' && e.rowNumber === raw.rowNumber);
      return {
        rowNumber: raw.rowNumber,
        status: 'error' as const,
        duplicateAction: 'add' as const,
        selected: false,
        data: {
          categoryName: raw.categoryName,
          faqCategoryName: raw.faqCategoryName,
          question: raw.question,
          answer: raw.answer,

          isInitial: raw.isInitial,
        },
        errorMessage: err?.message,
      };
    }

    const isNewParent = !productCategoryNameSet.has(raw.categoryName);
    const isNewSub = !faqCategoryNameMap.has(`${raw.categoryName}::${raw.faqCategoryName}`);
    const isDuplicate = existingFaqKeySet.has(
      `${raw.categoryName}::${raw.faqCategoryName}::${raw.question}`,
    );

    if (isNewParent && !seenNewParents.has(raw.categoryName)) {
      newCategories.push({ name: raw.categoryName, type: 'product' });
      seenNewParents.add(raw.categoryName);
    }
    if (isNewSub && !seenNewSubs.has(`${raw.categoryName}::${raw.faqCategoryName}`)) {
      newCategories.push({
        name: raw.faqCategoryName,
        type: 'faq',
        productCategoryName: raw.categoryName,
      });
      seenNewSubs.add(`${raw.categoryName}::${raw.faqCategoryName}`);
    }

    return {
      rowNumber: raw.rowNumber,
      status: isDuplicate ? ('duplicate' as const) : ('new' as const),
      duplicateAction: isDuplicate ? ('skip' as const) : ('add' as const),
      selected: true,
      data: {
        categoryName: raw.categoryName,
        faqCategoryName: raw.faqCategoryName,
        question: raw.question,
        answer: raw.answer,
        isInitial: raw.isInitial,
      },
      isNewCategory: isNewParent,
      isNewFaqCategory: isNewSub,
    };
  });

  const symptoms: ParsedSymptomRow[] = rawSymptoms.map((raw) => {
    if (errorRowsSymptom.has(raw.rowNumber)) {
      const err = allErrors.find((e) => e.sheet === 'symptom' && e.rowNumber === raw.rowNumber);
      return {
        rowNumber: raw.rowNumber,
        status: 'error' as const,
        duplicateAction: 'add' as const,
        selected: false,
        data: {
          categoryName: raw.categoryName,
          symptomName: raw.symptomName,
          cause: raw.cause,
          solution: raw.solution,
        },
        errorMessage: err?.message,
      };
    }

    const isDuplicate = existingSymptomKeySet.has(`${raw.categoryName}::${raw.symptomName}`);
    const isNewCat = !productCategoryNameSet.has(raw.categoryName);

    if (isNewCat && !seenNewParents.has(raw.categoryName)) {
      newCategories.push({ name: raw.categoryName, type: 'product' });
      seenNewParents.add(raw.categoryName);
    }

    return {
      rowNumber: raw.rowNumber,
      status: isDuplicate ? ('duplicate' as const) : ('new' as const),
      duplicateAction: isDuplicate ? ('skip' as const) : ('add' as const),
      selected: true,
      data: {
        categoryName: raw.categoryName,
        symptomName: raw.symptomName,
        cause: raw.cause,
        solution: raw.solution,
      },
      isNewCategory: isNewCat,
    };
  });

  return { faqs, symptoms, errors: allErrors, newCategories };
}

export function useExcelParser() {
  const [parseResult, setParseResult] = useState<ParseResult | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [isParsing, setIsParsing] = useState(false);

  const parseFile = useCallback(
    async (
      file: File,
      existingCategories: FaqCategory[],
      existingFaqs: FaqItem[],
      existingSymptoms: SymptomListItem[],
    ) => {
      setIsParsing(true);
      setParseError(null);
      try {
        const buffer = await file.arrayBuffer();
        const { rawFaqs, rawSymptoms } = await parseExcelBuffer(buffer);
        const faqErrors = validateFaqRows(rawFaqs);
        const symptomErrors = validateSymptomRows(rawSymptoms);
        const result = buildParseResult(
          rawFaqs,
          rawSymptoms,
          faqErrors,
          symptomErrors,
          existingCategories,
          existingFaqs,
          existingSymptoms,
        );
        setParseResult(result);
      } catch (err) {
        setParseError(err instanceof Error ? err.message : 'ファイルの読み込みに失敗しました');
      } finally {
        setIsParsing(false);
      }
    },
    [],
  );

  const reset = useCallback(() => {
    setParseResult(null);
    setParseError(null);
  }, []);

  return { parseResult, parseError, isParsing, parseFile, reset, setParseResult };
}
